import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { filter, map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageRoutingService {
  private supportedLanguages = ['en', 'sk', 'cz'];
  private defaultLanguage = 'en';
  private isBrowser: boolean;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translocoService: TranslocoService,
    private authService: AuthService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.initializeLanguageRouting();
  }

  private initializeLanguageRouting(): void {
    // Listen to route changes to extract language from URL
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.extractLanguageFromUrl())
      )
      .subscribe((lang) => {
        if (lang && this.supportedLanguages.includes(lang)) {
          this.translocoService.setActiveLang(lang);
          if (this.isBrowser) {
            localStorage.setItem('selectedLanguage', lang);
          }
        }
      });
  }

  /**
   * Extract language code from current URL
   */
  extractLanguageFromUrl(): string | null {
    const urlSegments = this.router.url.split('/').filter((segment) => segment);
    const firstSegment = urlSegments[0];

    if (firstSegment && this.supportedLanguages.includes(firstSegment)) {
      return firstSegment;
    }

    return null;
  }

  /**
   * Get current language from URL or fallback to default
   */
  getCurrentLanguage(): string {
    const langFromUrl = this.extractLanguageFromUrl();
    if (langFromUrl) {
      return langFromUrl;
    }

    // Fallback to stored language or default
    if (this.isBrowser) {
      const storedLang = localStorage.getItem('selectedLanguage');
      if (storedLang && this.supportedLanguages.includes(storedLang)) {
        return storedLang;
      }
    }

    return this.defaultLanguage;
  }

  /**
   * Navigate to a route with language prefix
   */
  navigateWithLanguage(route: string[], language?: string): void {
    const lang = language || this.getCurrentLanguage();
    const routeWithLang = [lang, ...route];
    this.router.navigate(routeWithLang);
  }

  /**
   * Change language and update URL
   */
  changeLanguage(newLanguage: string): void {
    if (!this.supportedLanguages.includes(newLanguage)) {
      console.warn(`Language ${newLanguage} is not supported`);
      return;
    }

    const currentUrl = this.router.url;
    const currentLang = this.extractLanguageFromUrl();

    let newUrl: string;

    if (currentLang) {
      // Replace current language in URL
      newUrl = currentUrl.replace(`/${currentLang}`, `/${newLanguage}`);
    } else {
      // Add language prefix to current URL
      newUrl = `/${newLanguage}${currentUrl === '/' ? '' : currentUrl}`;
    }

    // Update Transloco language
    this.translocoService.setActiveLang(newLanguage);

    // Store language preference
    if (this.isBrowser) {
      localStorage.setItem('selectedLanguage', newLanguage);
    }

    // Update user language on backend if user is logged in
    if (this.authService.isLoggedIn()) {
      this.authService.updateUserLanguage(newLanguage).subscribe({
        next: () => {
        },
        error: (err) => {
          console.error('Failed to update user language on server:', err);
        },
      });
    }

    // Navigate to new URL
    this.router.navigateByUrl(newUrl);
  }

  /**
   * Get localized route for a given path
   */
  getLocalizedRoute(path: string, language?: string): string {
    const lang = language || this.getCurrentLanguage();
    return `/${lang}${path.startsWith('/') ? path : '/' + path}`;
  }

  /**
   * Check if current route has language prefix
   */
  hasLanguagePrefix(): boolean {
    return this.extractLanguageFromUrl() !== null;
  }

  /**
   * Get supported languages
   */
  getSupportedLanguages(): string[] {
    return [...this.supportedLanguages];
  }

  /**
   * Get default language
   */
  getDefaultLanguage(): string {
    return this.defaultLanguage;
  }

  /**
   * Redirect to default language if no language is specified
   */
  redirectToDefaultLanguageIfNeeded(): void {
    const currentUrl = this.router.url;
    const hasLangPrefix = this.hasLanguagePrefix();

    // If URL doesn't have language prefix
    if (!hasLangPrefix) {
      const defaultLang = this.getCurrentLanguage();
      const newUrl = this.getLocalizedRoute(currentUrl, defaultLang);

      this.router.navigateByUrl(newUrl, { replaceUrl: true });
    }
  }
}
